/**
 * Actycrea: galleryscroll.esm.js (ES6)
 * MIT
 *
 * Turns the user's vertical scroll into a horizontal campaign gallery:
 * GSAP ScrollTrigger pins the section and translates the inner track
 * sideways as the page scrolls. Consumer: `module-gallery-scroll`
 * ( Iberia100 modules 5 and 9 ).
 *
 * The pin length equals the horizontal overflow of the track plus a short
 * lead-in "hold": once the section pins it stays still for a fraction of the
 * scroll before the horizontal reveal starts, so the entry feels settled
 * instead of jumping. Distances are resolved lazily ( function values +
 * `invalidateOnRefresh` ) so the effect stays correct after resizes and
 * lazy-loaded images.
 *
 * Honors `prefers-reduced-motion`: when set, no timeline is built and the
 * viewport falls back to native horizontal scroll ( see _gallery-scroll.scss ).
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PREFIX } from '../helpers/config.js';

gsap.registerPlugin( ScrollTrigger );

const ATTR_ROOT         = `data-${PREFIX}-gallery-scroll`;
const SELECTOR_DATA     = `[${ATTR_ROOT}]`;
const VIEWPORT_SELECTOR = '.gallery-scroll-viewport';
const TRACK_SELECTOR    = '.gallery-scroll-track';

const DEFAULTS = {
  // Lead-in hold before the horizontal motion begins, as a fraction of the
  // horizontal travel distance ( 0.15 = ~15% of the pinned scroll is held
  // still ). Raise it for a longer pause, lower it towards 0 to disable.
  holdRatio: 0.08 ,
  // Lead-out hold once the reveal finishes: the last image stays put for this
  // fraction of the travel distance before the pin releases and vertical
  // scroll resumes. Same units as `holdRatio`.
  holdEndRatio: 0.08 ,
  // Scrub smoothing ( seconds of catch-up ). Kept low so the hold doesn't lag.
  scrub: 0.6 ,
  // Disabled on purpose: `anticipatePin` pins the section slightly early in
  // proportion to scroll speed, which shows up as a jolt right before the
  // module on fast scroll. 0 pins exactly at `top top`.
  anticipatePin: 0
};

/**
 * One-time global setup guard ( normalizeScroll + refresh on full load ).
 *
 * @type {boolean}
 */
let globalSetupDone = false;

/**
 * Global ScrollTrigger setup, applied once and only if at least one
 * gallery-scroll module exists in the page.
 *
 * - `normalizeScroll( true )`: forces scroll handling onto
 *   requestAnimationFrame. On fast wheel/momentum scroll the browser jumps
 *   many pixels between frames, so the pin engages one frame late and the
 *   section snaps into place ( the perceived jolt ). Normalizing removes
 *   those inter-frame jumps — GSAP's documented fix for pin jolts.
 * - `refresh()` on window load: main.js boots at DOMContentLoaded, so
 *   lazy-loaded images can still change the document height afterwards and
 *   leave the pin start position stale ( another source of entry jumps ).
 *
 * @returns {void}
 */
function setupGlobal( ){
  if( globalSetupDone ) return;
  globalSetupDone = true;

  ScrollTrigger.normalizeScroll( true );

  if( document.readyState === 'complete' ){
    ScrollTrigger.refresh( );
  } else {
    window.addEventListener( 'load' , ( ) => ScrollTrigger.refresh( ) , { once: true } );
  }
}

export class GalleryScroll {
  /**
   * @param {HTMLElement} element - The module section ( pin trigger ).
   * @param {Object}      config  - Overrides for DEFAULTS ( holdRatio, scrub ).
   */
  constructor( element , config = { } ){
    if( !(element instanceof HTMLElement) ) throw new Error('GalleryScroll: element inválido');

    this._config   = { ...DEFAULTS , ...config };
    this._element  = element;
    this._viewport = element.querySelector( VIEWPORT_SELECTOR );
    this._track    = element.querySelector( TRACK_SELECTOR );
    this._timeline = null;

    if( !this._viewport || !this._track ) return;

    this._build( );
  }

  /**
   * Returns the horizontal overflow ( px ) the track must travel.
   *
   * @returns {number}
   */
  _distance( ){
    return Math.max( 0 , this._track.scrollWidth - this._viewport.clientWidth );
  }

  /**
   * Builds the pinned timeline: a still lead-in "hold", the horizontal
   * reveal, and a still lead-out "hold" on the last image.
   *
   * The scrub maps the pinned scroll range linearly onto the timeline, so the
   * three segments ( `holdRatio` : `1` : `holdEndRatio` ) split the scroll in
   * the same proportion. That is why `end` extends the pin by
   * `distance * ( 1 + holdRatio + holdEndRatio )`: each hold consumes
   * `distance * ratio` px of scroll ( the section stays put ) and the reveal
   * the remaining `distance` px.
   *
   * @returns {void}
   */
  _build( ){
    const holdRatio    = this._config.holdRatio;
    const holdEndRatio = this._config.holdEndRatio;

    this._timeline = gsap.timeline( {
      defaults: { ease: 'none' } ,
      scrollTrigger: {
        trigger: this._element ,
        start: 'top top' ,
        end: ( ) => '+=' + ( this._distance( ) * ( 1 + holdRatio + holdEndRatio ) ) ,
        scrub: this._config.scrub ,
        pin: true ,
        anticipatePin: this._config.anticipatePin ,
        invalidateOnRefresh: true
      }
    } );

    // Lead-in hold: pinned and still ( no horizontal shift yet ).
    this._timeline.to( this._track , { x: 0 , duration: holdRatio } );
    // Horizontal reveal.
    this._timeline.to( this._track , { x: ( ) => -this._distance( ) , duration: 1 } );
    // Lead-out hold: last image stays put before the pin releases.
    this._timeline.to( this._track , { x: ( ) => -this._distance( ) , duration: holdEndRatio } );
  }

  /**
   * Kills the timeline and its ScrollTrigger instance.
   *
   * @returns {void}
   */
  destroy( ){
    if( this._timeline ){
      if( this._timeline.scrollTrigger ) this._timeline.scrollTrigger.kill( );
      this._timeline.kill( );
      this._timeline = null;
    }
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    // Respect the user's motion preference: skip the pinned timeline entirely.
    if( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ){
      return [ ];
    }

    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if( el._galleryScrollInstance ) return;
      el._galleryScrollInstance = new GalleryScroll( el );
      instances.push( el._galleryScrollInstance );
    } );

    if( instances.length > 0 ){
      setupGlobal( );
    }

    return instances;
  }
}

export default GalleryScroll;
